import React, { useState, useEffect, useCallback } from 'react';
import {
    getTeams, bulkImportTeams,
    getTournamentTeams, addTournamentTeam, removeTournamentTeam,
    fetchSeedImportPreview, bulkAssignTournamentTeams
} from '../services/api';

const REGIONS = ['East', 'West', 'South', 'Midwest'];
const SEEDS   = Array.from({ length: 16 }, (_, i) => i + 1);

function AdminSeeding({ showFeedback, activeTournament }) {
    const [teams,           setTeams]           = useState([]);
    const [tournamentTeams, setTournamentTeams] = useState([]);
    const [activeRegion,    setActiveRegion]    = useState('East');
    const [search,          setSearch]          = useState('');
    const [selectedSeed,    setSelectedSeed]    = useState(null);

    // Bulk-seed panel: two ways in (paste, or auto-fetch from NCAA.com),
    // one shared preview table, one explicit confirm step before anything
    // is written. Auto-fetch only ever populates bulkPreview -- it never
    // calls the commit endpoint itself.
    const [bulkPanelOpen, setBulkPanelOpen] = useState(false);
    const [bulkText,      setBulkText]      = useState('');
    const [bulkStartDate, setBulkStartDate] = useState('');
    const [bulkEndDate,   setBulkEndDate]   = useState('');
    const [bulkPreview,   setBulkPreview]   = useState([]);
    const [bulkFetching,  setBulkFetching]  = useState(false);
    const [bulkAssigning, setBulkAssigning] = useState(false);
    const [bulkError,     setBulkError]     = useState('');
    const [creatingTeams, setCreatingTeams] = useState(false);
    const [regionDisplayNames, setRegionDisplayNames] = useState(null);

    // Prefer a mapping just discovered by this session's fetch (not yet
    // saved) over whatever's already persisted on the tournament, so the
    // region tabs/labels reflect a fresh fetch immediately, without
    // waiting on the parent to refresh its own tournament list after a
    // save. Falls back to plain internal names (East/West/South/Midwest)
    // when neither is set -- unchanged behavior for every men's
    // tournament and any women's tournament that hasn't been re-seeded
    // since this feature shipped.
    const effectiveRegionNames = regionDisplayNames || activeTournament?.region_display_names || null;
    const regionLabel = (region) => effectiveRegionNames?.[region] || region;

    useEffect(() => {
        getTeams().then(res => setTeams(res.data));
    }, []);

    useEffect(() => {
        if (!activeTournament) return;
        loadTournamentTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTournament]);

    const loadTournamentTeams = useCallback(() => {
        getTournamentTeams(activeTournament.id).then(res => setTournamentTeams(res.data));
    }, [activeTournament]);

    const getTeamForSlot = (region, seed) => {
        return tournamentTeams.find(tt => tt.region === region && tt.seed === seed);
    };

    const handleSlotClick = (seed) => {
        setSelectedSeed(seed);
        setSearch('');
    };

    const handleAssignTeam = async (team) => {
        if (!selectedSeed) return;
        try {
            await addTournamentTeam(activeTournament.id, {
                team_id: team.id,
                seed:    selectedSeed,
                region:  activeRegion
            });
            showFeedback(`${team.name} assigned to ${regionLabel(activeRegion)} ${selectedSeed}-seed`);
            setSelectedSeed(null);
            setSearch('');
            loadTournamentTeams();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to assign team', 'error');
        }
    };

    const handleRemoveTeam = async (tt) => {
        const confirmed = window.confirm(
            `Remove ${tt.team_name} from this tournament? If games have already been generated, this may affect existing matchups.`
        );
        if (!confirmed) return;
        try {
            await removeTournamentTeam(activeTournament.id, tt.id);
            showFeedback(`${tt.team_name} removed`);
            loadTournamentTeams();
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to remove team', 'error');
        }
    };

    const seededTeamIds = tournamentTeams.map(tt => tt.team_id);
    const filteredTeams = teams.filter(t =>
        !seededTeamIds.includes(t.id) &&
        (t.name.toLowerCase().includes(search.toLowerCase()) ||
         (t.conference || '').toLowerCase().includes(search.toLowerCase()))
    );

    // Resolves a team name against the already-loaded master teams list.
    // Tries, in order: exact match against name, exact match against
    // short_name -- deliberately NOT fuzzy, since a wrong silent match
    // here would assign the wrong team to a seed slot. An unmatched name
    // shows up in the preview for manual resolution (add it to the
    // master list first via AdminTeams.js's bulk import, then re-run
    // this), never guessed.
    const resolveTeamByName = (name, fullName) => {
        const lower = (name || '').trim().toLowerCase();
        const lowerFull = (fullName || '').trim().toLowerCase();
        const match = teams.find(t =>
            t.name.toLowerCase() === lower ||
            (t.short_name && t.short_name.toLowerCase() === lower) ||
            (lowerFull && t.name.toLowerCase() === lowerFull)
        );
        return match ? match.id : null;
    };

    // Existing assignment for a region+seed, if any -- used to flag when
    // a bulk commit would silently overwrite an already-seeded slot
    // (the backend upserts on (tournament_id, region, seed), so it has
    // no other way to know this was unintentional).
    const existingForSlot = (region, seed) =>
        tournamentTeams.find(tt => tt.region === region && tt.seed === seed);

    // Annotates a raw preview row with what committing it would actually
    // do: 'empty' (new assignment, nothing lost), 'same' (already exactly
    // this team, a harmless no-op), or 'overwrite' (a DIFFERENT team
    // currently occupies this slot and would be replaced).
    const withOverwriteStatus = (rows) => rows.map(p => {
        const existing = p.matched_team_id ? existingForSlot(p.region, p.seed) : null;
        let slot_status = 'empty';
        if (existing) {
            slot_status = existing.team_id === p.matched_team_id ? 'same' : 'overwrite';
        }
        return { ...p, existing_team_name: existing?.team_name || null, slot_status };
    });

    const handleParsePaste = () => {
        setBulkError('');
        setRegionDisplayNames(null);
        const rows = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
        const parsed = [];
        const badLines = [];
        for (const line of rows) {
            const parts = line.split(',').map(p => p.trim());
            if (parts.length !== 3) { badLines.push(line); continue; }
            const [region, seedStr, teamName] = parts;
            const seed = parseInt(seedStr, 10);
            if (!REGIONS.includes(region) || !seed || seed < 1 || seed > 16 || !teamName) {
                badLines.push(line);
                continue;
            }
            parsed.push({ region, seed, team_name: teamName, matched_team_id: resolveTeamByName(teamName) });
        }
        if (badLines.length > 0) {
            setBulkError(`${badLines.length} line(s) couldn't be parsed (expected "Region,Seed,TeamName") and were skipped: ${badLines.slice(0, 3).join(' | ')}${badLines.length > 3 ? '...' : ''}`);
        }
        setBulkPreview(withOverwriteStatus(parsed));
    };

    const handleFetchFromNcaa = async () => {
        if (!bulkStartDate || !bulkEndDate) {
            setBulkError('Enter both a start and end date -- a few days covering Selection Sunday week is fine, you don\'t need the exact Round of 64 dates.');
            return;
        }
        setBulkFetching(true);
        setBulkError('');
        setRegionDisplayNames(null);
        try {
            const res = await fetchSeedImportPreview(activeTournament.id, bulkStartDate, bulkEndDate);
            // Re-resolve against the currently-loaded teams list client-side
            // too (the backend already resolved it, but this keeps the
            // preview live if the person edits bulkText/teams afterward).
            const preview = res.data.preview.map(p => ({
                ...p,
                matched_team_id: p.matched_team_id ?? resolveTeamByName(p.team_name, p.team_name_full)
            }));
            setBulkPreview(withOverwriteStatus(preview));
            setRegionDisplayNames(res.data.region_display_names || null);
            if (res.data.unmatched_count > 0) {
                setBulkError(`${res.data.unmatched_count} team name(s) from NCAA.com don't match anything in your master team list yet. They'll be skipped if you click Assign now -- add them via Teams first, then re-fetch to pick them up.`);
            }
        } catch (err) {
            setBulkError(err.response?.data?.error || 'Failed to fetch from NCAA.com -- you can still paste seeds manually below.');
        } finally {
            setBulkFetching(false);
        }
    };

    const handleCommitBulk = async () => {
        const matched = bulkPreview.filter(p => p.matched_team_id);
        if (matched.length === 0) {
            setBulkError('Nothing to assign -- every row is unmatched.');
            return;
        }

        const overwrites = matched.filter(p => p.slot_status === 'overwrite');
        if (overwrites.length > 0) {
            const preview = overwrites.slice(0, 8)
                .map(p => `${regionLabel(p.region)} #${p.seed}: ${p.existing_team_name} → ${p.team_name}`)
                .join('\n');
            const confirmed = window.confirm(
                `This will OVERWRITE ${overwrites.length} already-seeded slot(s) in ${activeTournament.name}:\n\n` +
                `${preview}${overwrites.length > 8 ? `\n...and ${overwrites.length - 8} more` : ''}\n\n` +
                `This cannot be undone from here. Continue?`
            );
            if (!confirmed) return;
        }

        setBulkAssigning(true);
        try {
            const assignments = matched.map(p => ({ team_id: p.matched_team_id, seed: p.seed, region: p.region }));
            const res = await bulkAssignTournamentTeams(activeTournament.id, assignments, regionDisplayNames);
            const skipped = bulkPreview.length - matched.length;
            const regionNote = regionDisplayNames ? ' Region names saved for this tournament.' : '';
            showFeedback(`${res.data.assigned} team(s) assigned${skipped > 0 ? `, ${skipped} skipped (unmatched)` : ''}.${regionNote}`);
            setBulkPanelOpen(false);
            setBulkPreview([]);
            setBulkText('');
            loadTournamentTeams();
        } catch (err) {
            setBulkError(err.response?.data?.error || 'Failed to assign teams');
        } finally {
            setBulkAssigning(false);
        }
    };

    const handleCreateMissingTeams = async () => {
        const unmatched = bulkPreview.filter(p => !p.matched_team_id);
        if (unmatched.length === 0) return;

        // One entry per distinct team name -- a name could appear more
        // than once across regions/seeds in edge cases (shouldn't happen
        // in real bracket data, but paste input is free text).
        const seen = new Set();
        const toCreate = [];
        for (const p of unmatched) {
            const name = p.team_name_full || p.team_name;
            if (seen.has(name.toLowerCase())) continue;
            seen.add(name.toLowerCase());
            const shortName = (p.team_name_full && p.team_name_full !== p.team_name) ? p.team_name : null;
            toCreate.push({ name, short_name: shortName, conference: p.conference || null });
        }

        setCreatingTeams(true);
        try {
            const res = await bulkImportTeams(toCreate);
            const teamsRes = await getTeams();
            setTeams(teamsRes.data);
            // Re-resolve the whole preview against the now-updated master
            // list, using the same multi-field logic as everywhere else.
            const reResolved = bulkPreview.map(p => ({
                ...p,
                matched_team_id: p.matched_team_id || (() => {
                    const match = teamsRes.data.find(t =>
                        t.name.toLowerCase() === (p.team_name || '').toLowerCase() ||
                        (t.short_name && t.short_name.toLowerCase() === (p.team_name || '').toLowerCase()) ||
                        t.name.toLowerCase() === (p.team_name_full || '').toLowerCase()
                    );
                    return match ? match.id : null;
                })()
            }));
            setBulkPreview(withOverwriteStatus(reResolved));
            showFeedback(`${res.data.inserted} team(s) added to your master list`);
            setBulkError('');
        } catch (err) {
            setBulkError(err.response?.data?.error || 'Failed to create teams -- only a platform admin can add to the master team list.');
        } finally {
            setCreatingTeams(false);
        }
    };

    const regionComplete = (region) => {
        const count = tournamentTeams.filter(tt => tt.region === region).length;
        return count === 16;
    };

    if (!activeTournament) return <p className="text-gray-500 text-sm">No tournament selected.</p>;

    return (
        <div>
            {activeTournament && (
                <div className="mb-4 bg-gray-900 border border-orange-500/40 rounded-lg px-4 py-2 inline-flex items-center gap-2">
                    <span className="text-gray-500 text-xs uppercase tracking-wide">Now seeding</span>
                    <span className="text-white font-bold text-sm">{activeTournament.name}</span>
                    <span className={'text-xs font-bold px-2 py-0.5 rounded-full ' +
                        (activeTournament.gender === 'W' ? 'bg-pink-900 text-pink-300' : 'bg-blue-900 text-blue-300')}>
                        {activeTournament.gender === 'W' ? "Women's" : "Men's"}
                    </span>
                </div>
            )}

            <div className="flex gap-3 mb-6 flex-wrap items-center justify-between">
                <span className="text-gray-500 text-sm">
                    {tournamentTeams.length} / {(activeTournament?.total_teams || 64)} teams seeded
                </span>
                <button
                    onClick={() => setBulkPanelOpen(o => !o)}
                    className="text-sm font-bold px-3 py-1.5 rounded-lg bg-gray-800 text-orange-400 hover:bg-gray-700 transition-colors">
                    {bulkPanelOpen ? 'Close bulk seed' : 'Bulk seed'}
                </button>
            </div>

            {bulkPanelOpen && (
                <div className="mb-6 bg-gray-900 border border-orange-500/40 rounded-xl p-5">
                    <h3 className="text-lg font-black text-white mb-1">Bulk seed</h3>
                    <p className="text-gray-500 text-xs mb-4">
                        Nothing is saved until you click Assign, and Assign only acts on rows marked "matched" --
                        an unmatched row is skipped entirely and its seed slot is left untouched. Team names must
                        exactly match your master Teams list (Admin → Teams); if a name is missing there, add it
                        on the Teams tab first, then come back and re-run the fetch or paste to pick it up.
                    </p>

                    {tournamentTeams.length > 0 && (
                        <div className="bg-yellow-900/30 border border-yellow-600 text-yellow-300 text-sm rounded-lg px-4 py-3 mb-4 font-bold">
                            ⚠ This tournament already has {tournamentTeams.length} of {(activeTournament?.total_teams || 64)} seed slots filled.
                            Any row below marked "will overwrite" replaces an existing team -- review carefully before assigning.
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-800/50 rounded-lg p-4">
                            <p className="text-white text-sm font-bold mb-2">Option 1: Paste</p>
                            <p className="text-gray-500 text-xs mb-2">One team per line: Region,Seed,TeamName</p>
                            <textarea
                                value={bulkText}
                                onChange={e => setBulkText(e.target.value)}
                                placeholder={'East,1,Kansas\nEast,16,American'}
                                rows={5}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-orange-500 mb-2"
                            />
                            <button
                                onClick={handleParsePaste}
                                disabled={!bulkText.trim()}
                                className="text-sm font-bold px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                                Preview pasted teams
                            </button>
                        </div>

                        <div className="bg-gray-800/50 rounded-lg p-4">
                            <p className="text-white text-sm font-bold mb-2">Option 2: Fetch from NCAA.com</p>
                            <p className="text-gray-500 text-xs mb-2">
                                A date range covering Selection Sunday week is enough -- you don't need
                                the exact Round of 64 dates, any day with no games just contributes nothing.
                                Unofficial source -- always review the preview before assigning.
                            </p>
                            <div className="flex gap-2 mb-2 items-center">
                                <input type="date" value={bulkStartDate}
                                    onChange={e => setBulkStartDate(e.target.value)}
                                    className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500" />
                                <span className="text-gray-500 text-xs">to</span>
                                <input type="date" value={bulkEndDate}
                                    onChange={e => setBulkEndDate(e.target.value)}
                                    className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500" />
                            </div>
                            <button
                                onClick={handleFetchFromNcaa}
                                disabled={bulkFetching}
                                className="text-sm font-bold px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                                {bulkFetching ? 'Fetching…' : 'Fetch preview'}
                            </button>
                        </div>
                    </div>

                    {regionDisplayNames && (
                        <div className="bg-blue-900/30 border border-blue-700 text-blue-300 text-xs rounded-lg px-3 py-2 mb-4">
                            NCAA.com uses different region names for this tournament (this is normal for the
                            women's bracket, which is named by host site rather than direction):
                            {' '}{REGIONS.map(r => `${regionDisplayNames[r] || r} → ${r}`).join(', ')}.
                            {' '}These will be saved as this tournament's region labels once you click Assign below.
                        </div>
                    )}

                    {bulkError && (
                        <div className="bg-red-900/30 border border-red-700 text-red-300 text-xs rounded-lg px-3 py-2 mb-4">
                            {bulkError}
                        </div>
                    )}

                    {bulkPreview.some(p => !p.matched_team_id) && (
                        <div className="bg-gray-800/50 rounded-lg p-3 mb-4 flex items-center justify-between gap-3 flex-wrap">
                            <p className="text-gray-400 text-xs">
                                {bulkPreview.filter(p => !p.matched_team_id).length} unmatched name(s) can be added
                                to your master Teams list directly from this preview, using the names (and
                                conference, when known) NCAA.com reported.
                            </p>
                            <button
                                onClick={handleCreateMissingTeams}
                                disabled={creatingTeams}
                                className="text-sm font-bold px-3 py-1.5 rounded-lg bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap">
                                {creatingTeams ? 'Creating…' : `Create ${bulkPreview.filter(p => !p.matched_team_id).length} missing team(s)`}
                            </button>
                        </div>
                    )}

                    {bulkPreview.length > 0 && (
                        <div>
                            <p className="text-white text-sm font-bold mb-2">
                                Preview ({bulkPreview.filter(p => p.matched_team_id).length} of {bulkPreview.length} matched)
                                {bulkPreview.some(p => p.slot_status === 'overwrite') && (
                                    <span className="ml-2 text-red-400 font-normal">
                                        — {bulkPreview.filter(p => p.slot_status === 'overwrite').length} will overwrite an existing seed
                                    </span>
                                )}
                            </p>
                            <div className="max-h-64 overflow-y-auto border border-gray-800 rounded-lg mb-3">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-800 text-gray-400 text-xs uppercase sticky top-0">
                                        <tr>
                                            <th className="text-left px-3 py-2">Region</th>
                                            <th className="text-left px-3 py-2">Seed</th>
                                            <th className="text-left px-3 py-2">Team</th>
                                            <th className="text-left px-3 py-2">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bulkPreview.map((p, i) => (
                                            <tr key={i} className={'border-t border-gray-800 ' + (p.slot_status === 'overwrite' ? 'bg-red-950/40' : '')}>
                                                <td className="px-3 py-1.5 text-gray-300">{regionLabel(p.region)}</td>
                                                <td className="px-3 py-1.5 text-gray-300">{p.seed}</td>
                                                <td className="px-3 py-1.5 text-white">
                                                    {p.team_name}
                                                    {!p.matched_team_id && p.team_name_full && p.team_name_full !== p.team_name && (
                                                        <span className="block text-gray-500 text-xs">{p.team_name_full}</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5">
                                                    {!p.matched_team_id ? (
                                                        <span className="text-yellow-500">⚠ no match</span>
                                                    ) : p.slot_status === 'overwrite' ? (
                                                        <span className="text-red-400 font-bold">⚠ will overwrite: {p.existing_team_name}</span>
                                                    ) : p.slot_status === 'same' ? (
                                                        <span className="text-gray-500">✓ already set (no change)</span>
                                                    ) : (
                                                        <span className="text-green-400">✓ matched</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button
                                onClick={handleCommitBulk}
                                disabled={bulkAssigning || bulkPreview.every(p => !p.matched_team_id)}
                                className={'text-sm font-bold px-4 py-2 rounded-lg text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors ' +
                                    (bulkPreview.some(p => p.slot_status === 'overwrite') ? 'bg-red-600 hover:bg-red-500' : 'bg-green-600 hover:bg-green-500')}>
                                {bulkAssigning ? 'Assigning…' : `Assign ${bulkPreview.filter(p => p.matched_team_id).length} team(s)`}
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className="flex gap-2 mb-6">
                {REGIONS.map(r => (
                    <button key={r} onClick={() => { setActiveRegion(r); setSelectedSeed(null); }}
                        className={'px-4 py-2 rounded-lg text-sm font-bold transition-colors ' +
                            (activeRegion === r
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-800 text-gray-400 hover:text-white')}>
                        {regionLabel(r)}
                        {regionComplete(r) && <span className="ml-1 text-green-400">✓</span>}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                    <h3 className="text-lg font-black text-white mb-4">
                        {regionLabel(activeRegion)} Region
                        {selectedSeed && (
                            <span className="ml-2 text-sm font-normal text-orange-400">
                                — selecting seed {selectedSeed}
                            </span>
                        )}
                    </h3>
                    <div className="space-y-2">
                        {SEEDS.map(seed => {
                            const tt = getTeamForSlot(activeRegion, seed);
                            const isSelected = selectedSeed === seed;
                            return (
                                <div key={seed}
                                    onClick={() => !tt && handleSlotClick(seed)}
                                    className={'flex items-center gap-3 p-3 rounded-lg border transition-colors ' +
                                        (isSelected
                                            ? 'border-orange-500 bg-gray-800 cursor-pointer'
                                            : tt
                                                ? 'border-gray-700 bg-gray-800'
                                                : 'border-dashed border-gray-700 hover:border-gray-500 cursor-pointer')}>
                                    <span className={'text-sm font-black w-6 text-center ' +
                                        (tt ? 'text-orange-400' : 'text-gray-600')}>
                                        {seed}
                                    </span>
                                    {tt ? (
                                        <div className="flex items-center justify-between flex-1">
                                            <div>
                                                <p className="text-white font-bold text-sm">{tt.team_name}</p>
                                                <p className="text-gray-500 text-xs">{tt.conference}</p>
                                            </div>
                                            <button
                                                onClick={e => { e.stopPropagation(); handleRemoveTeam(tt); }}
                                                className="text-gray-600 hover:text-red-400 text-xs px-2 py-1 rounded transition-colors">
                                                Remove
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-gray-600 text-sm">
                                            {isSelected ? 'Search for a team →' : 'Click to assign'}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                    <h3 className="text-lg font-black text-white mb-4">
                        Available teams
                        <span className="ml-2 text-sm font-normal text-gray-500">
                            ({filteredTeams.length} unassigned)
                        </span>
                    </h3>
                    <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search teams..."
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500 mb-4"
                    />
                    {!selectedSeed && (
                        <p className="text-gray-600 text-xs mb-3">
                            Click an empty seed slot on the left to assign a team.
                        </p>
                    )}
                    {selectedSeed && (
                        <p className="text-orange-400 text-xs mb-3">
                            Click a team to assign it to {regionLabel(activeRegion)} seed {selectedSeed}.
                        </p>
                    )}
                    <div className="space-y-1 max-h-screen overflow-y-auto">
                        {filteredTeams.map(t => (
                            <div key={t.id}
                                onClick={() => selectedSeed && handleAssignTeam(t)}
                                className={'flex items-center justify-between p-2 rounded-lg transition-colors ' +
                                    (selectedSeed
                                        ? 'hover:bg-gray-800 cursor-pointer border border-transparent hover:border-orange-500'
                                        : 'opacity-50 cursor-not-allowed')}>
                                <div>
                                    <p className="text-white text-sm font-medium">{t.name}</p>
                                    <p className="text-gray-500 text-xs">{t.conference}</p>
                                </div>
                                <span className="text-gray-600 text-xs">{t.short_name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminSeeding;